/**
 * Performance metrics collector for M0.
 *
 * Tracks:
 *  - Frame deltas via requestAnimationFrame → fps p50 / p95 (rolling 5s window)
 *  - Page-load timing (FCP, LCP, INP, CLS, DOMContentLoaded, load, first frame)
 *  - Error count (incremented by the global error handlers)
 *
 * Design notes:
 *  - Pure data, no DOM mutation; UI rendering is the caller's problem
 *  - Lightweight, no allocations in the hot path (the rAF callback)
 *  - Deterministic enough to be unit-tested with a fake rAF clock
 *  - No external network calls; metrics are kept in-memory and surfaced
 *    via `subscribe` / `getReport` so the caller decides what to do
 *    (print, send to analytics, etc.)
 */

export interface FpsReport {
  /** Rolling window size in seconds. */
  windowSeconds: number;
  /** Sample count in the current window. */
  sampleCount: number;
  /** Median frame time in ms. */
  p50Ms: number;
  /** 95th-percentile frame time in ms. */
  p95Ms: number;
  /** Median fps (1000 / p50Ms). */
  fpsP50: number;
  /** 95th-percentile fps (1000 / p95Ms). */
  fpsP95: number;
  /** Worst (max) frame time seen in the window. */
  maxMs: number;
}

export interface LoadTimingReport {
  /** Milliseconds from `performance.timeOrigin` to `DOMContentLoaded`. */
  domContentLoadedMs: number | null;
  /** Milliseconds from `performance.timeOrigin` to `load`. */
  loadMs: number | null;
  /** First Contentful Paint. */
  fcpMs: number | null;
  /** Largest Contentful Paint (latest value reported by web-vitals). */
  lcpMs: number | null;
  /** Interaction to Next Paint (latest value reported by web-vitals). */
  inpMs: number | null;
  /** Cumulative Layout Shift (latest value reported by web-vitals). */
  clsScore: number | null;
  /** Milliseconds from timeOrigin to the first rendered frame. */
  firstFrameMs: number | null;
  /** Bytes transferred for all resource entries on the current page. */
  transferSizeBytes: number | null;
}

export interface MetricsReport {
  load: LoadTimingReport;
  fps: FpsReport;
  errorCount: number;
  /** Wall-clock timestamp when the snapshot was taken. */
  capturedAt: number;
}

export interface MetricsCollector {
  /** Start sampling frame deltas via requestAnimationFrame. Idempotent. */
  startFpsMeter(): void;
  /** Stop sampling. Idempotent. */
  stopFpsMeter(): void;
  /** Mark the first frame as rendered; sets firstFrameMs once. */
  recordFirstFrame(): void;
  /** Increment the error counter. */
  recordError(): void;
  /** External adapter for web-vitals' First Contentful Paint callback. */
  recordFcp(valueMs: number): void;
  /** External adapter for web-vitals' Largest Contentful Paint callback. */
  recordLcp(valueMs: number): void;
  /** External adapter for web-vitals' Interaction to Next Paint callback. */
  recordInp(valueMs: number): void;
  /** External adapter for web-vitals' Cumulative Layout Shift callback. */
  recordCls(value: number): void;
  /** Snapshot of the current fps state. */
  getFpsReport(): FpsReport;
  /** Snapshot of the current load timing. */
  getLoadTiming(): LoadTimingReport;
  /** Number of uncaught errors / unhandled rejections observed. */
  getErrorCount(): number;
  /** Combined report. */
  getReport(): MetricsReport;
  /** Subscribe to report changes. Returns an unsubscribe function. */
  subscribe(listener: (report: MetricsReport) => void): () => void;
}

export interface CreateMetricsCollectorOptions {
  /** Rolling window for fps samples, in seconds. */
  fpsWindowSeconds?: number;
  /** Override the rAF implementation (used in tests). */
  raf?: (cb: (now: number) => void) => number;
  /** Override the cAF implementation (used in tests). */
  caf?: (handle: number) => void;
  /** Override performance.now (used in tests). */
  now?: () => number;
  /** Override the Performance API (used in tests). */
  performance?: Performance | null;
  /** Document reference for readyState / event listeners. */
  doc?: Document | null;
  /** Window reference for load events. */
  win?: Window | null;
}

const DEFAULT_WINDOW_SECONDS = 5;

export function createMetricsCollector(
  options: CreateMetricsCollectorOptions = {},
): MetricsCollector {
  const windowSeconds = options.fpsWindowSeconds ?? DEFAULT_WINDOW_SECONDS;
  const raf = options.raf ?? requestAnimationFrame.bind(globalThis);
  const caf = options.caf ?? cancelAnimationFrame.bind(globalThis);
  const now = options.now ?? (() => performance.now());
  const perf: Performance | null =
    options.performance !== undefined
      ? options.performance
      : typeof performance !== 'undefined'
        ? performance
        : null;
  const doc: Document | null =
    options.doc !== undefined ? options.doc : typeof document !== 'undefined' ? document : null;
  const win: Window | null =
    options.win !== undefined ? options.win : typeof window !== 'undefined' ? window : null;

  // Frame timing state. `samples` stores (timestamp, deltaMs) tuples so the
  // rolling-window prune can compare against the absolute timestamp and
  // the percentile calculation can read the deltas.
  let samples: Array<{ ts: number; dt: number }> = [];
  let lastFrameMs: number | null = null;
  let rafHandle: number | null = null;
  let running = false;

  // Load timing state
  let domContentLoadedMs: number | null = null;
  let loadMs: number | null = null;
  let fcpMs: number | null = null;
  let lcpMs: number | null = null;
  let inpMs: number | null = null;
  let clsScore: number | null = null;
  let firstFrameMs: number | null = null;
  let transferSizeBytes: number | null = null;

  // Error counter
  let errorCount = 0;

  // Subscribers
  const listeners = new Set<(report: MetricsReport) => void>();

  function notify(): void {
    if (listeners.size === 0) return;
    const report = getReport();
    for (const listener of listeners) {
      try {
        listener(report);
      } catch (err) {
        // Listeners must not break the collector
        console.error('[metrics] subscriber threw', err);
      }
    }
  }

  function onFrame(timestamp: number): void {
    if (!running) return;
    if (lastFrameMs !== null) {
      const delta = timestamp - lastFrameMs;
      // Discard absurd values (tab returning from background, debugger pauses)
      if (delta > 0 && delta < 1000) {
        samples.push({ ts: timestamp, dt: delta });
      }
    }
    lastFrameMs = timestamp;
    pruneSamples(timestamp);
    rafHandle = raf(onFrame);
  }

  function pruneSamples(timestamp: number): void {
    const cutoff = timestamp - windowSeconds * 1000;
    // samples are appended in order, so drop from the front while stale
    let i = 0;
    while (i < samples.length && samples[i]!.ts < cutoff) {
      i += 1;
    }
    if (i > 0) samples = samples.slice(i);
  }

  function startFpsMeter(): void {
    if (running) return;
    running = true;
    lastFrameMs = null;
    samples = [];
    rafHandle = raf(onFrame);
  }

  function stopFpsMeter(): void {
    if (!running) return;
    running = false;
    if (rafHandle !== null) {
      caf(rafHandle);
      rafHandle = null;
    }
  }

  function recordFirstFrame(): void {
    if (firstFrameMs !== null) return;
    firstFrameMs = now();
    notify();
  }

  function recordError(): void {
    errorCount += 1;
    notify();
  }

  function recordFcp(valueMs: number): void {
    if (fcpMs === null || valueMs < fcpMs) fcpMs = valueMs;
    notify();
  }

  function recordLcp(valueMs: number): void {
    // web-vitals may re-emit LCP as the largest element changes
    lcpMs = valueMs;
    notify();
  }

  function recordInp(valueMs: number): void {
    // Report the worst (highest) INP
    if (inpMs === null || valueMs > inpMs) inpMs = valueMs;
    notify();
  }

  function recordCls(value: number): void {
    // CLS is cumulative
    clsScore = (clsScore ?? 0) + value;
    notify();
  }

  function getFpsReport(): FpsReport {
    if (samples.length === 0) {
      return {
        windowSeconds,
        sampleCount: 0,
        p50Ms: 0,
        p95Ms: 0,
        fpsP50: 0,
        fpsP95: 0,
        maxMs: 0,
      };
    }
    const deltas = samples.map((s) => s.dt).sort((a, b) => a - b);
    const p50 = percentile(deltas, 0.5);
    const p95 = percentile(deltas, 0.95);
    const max = deltas[deltas.length - 1] ?? 0;
    return {
      windowSeconds,
      sampleCount: deltas.length,
      p50Ms: p50,
      p95Ms: p95,
      fpsP50: p50 > 0 ? 1000 / p50 : 0,
      fpsP95: p95 > 0 ? 1000 / p95 : 0,
      maxMs: max,
    };
  }

  function getLoadTiming(): LoadTimingReport {
    return {
      domContentLoadedMs,
      loadMs,
      fcpMs,
      lcpMs,
      inpMs,
      clsScore,
      firstFrameMs,
      transferSizeBytes,
    };
  }

  function getErrorCount(): number {
    return errorCount;
  }

  function getReport(): MetricsReport {
    return {
      load: getLoadTiming(),
      fps: getFpsReport(),
      errorCount,
      capturedAt: now(),
    };
  }

  function subscribe(listener: (report: MetricsReport) => void): () => void {
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  }

  function collectResourceSizes(): void {
    if (!perf || typeof perf.getEntriesByType !== 'function') return;
    let total = 0;
    const entries = perf.getEntriesByType('resource');
    for (const e of entries) {
      const size = (e as PerformanceResourceTiming).transferSize;
      if (typeof size === 'number' && size > 0) total += size;
    }
    transferSizeBytes = total > 0 ? total : null;
  }

  // Wire load-timing observers. Safe to call once; collectors that need
  // pre-bootstrap init can pass a custom `performance` / `doc` override.
  if (doc) {
    const onDomContentLoaded = (): void => {
      const entry = perf?.getEntriesByName?.('domcontentloaded')[0];
      domContentLoadedMs = entry ? entry.startTime : perf ? perf.now() : now();
      notify();
    };
    const onLoad = (): void => {
      const entry = perf?.getEntriesByName?.('load')[0];
      loadMs = entry ? entry.startTime : perf ? perf.now() : now();
      collectResourceSizes();
      notify();
    };
    if (doc.readyState === 'complete' || doc.readyState === 'interactive') {
      onDomContentLoaded();
    } else {
      doc.addEventListener('DOMContentLoaded', onDomContentLoaded, { once: true });
    }
    if (doc.readyState === 'complete') {
      onLoad();
    } else if (win) {
      win.addEventListener('load', onLoad, { once: true });
    }
  }

  return {
    startFpsMeter,
    stopFpsMeter,
    recordFirstFrame,
    recordError,
    recordFcp,
    recordLcp,
    recordInp,
    recordCls,
    getFpsReport,
    getLoadTiming,
    getErrorCount,
    getReport,
    subscribe,
  };
}

/** Linear-interpolated percentile on a sorted ascending array. */
export function percentile(sortedAsc: number[], p: number): number {
  if (sortedAsc.length === 0) return 0;
  if (sortedAsc.length === 1) return sortedAsc[0]!;
  const rank = p * (sortedAsc.length - 1);
  const lower = Math.floor(rank);
  const upper = Math.ceil(rank);
  if (lower === upper) return sortedAsc[lower]!;
  const weight = rank - lower;
  return sortedAsc[lower]! * (1 - weight) + sortedAsc[upper]! * weight;
}
